import * as React from "react"
import { useParams } from "react-router-dom"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { Form } from "antd"
import { Select, TextAreaEdit } from "components-design-system"
import WorkflowModalHeading from "components/WorkflowModalForm/WorkflowModalHeading/WorkflowModalHeading"
import FileTypeField from "./FileTypeField"
import "./FilesFormsWorkFields.scss"

// api
// "activity" model
import { useGetActivity } from "api/hooks/activities/useActivitiesApi"
import { useGetAuthoringVersions } from "api/hooks/studies/useAuthoringApi"
import { useGetCommentTypes } from "api/hooks/admin/useCommentTypesApi"
import { useGetStudyDocuments } from "api/hooks/studies/useDocumentsApi"
import { useUserSettings } from "api/hooks"
// "request" model
import { useGetRequestDocuments } from "api/hooks/requests/requestsDocumentsApis"

const FilesFormsWorkFields = ({
  form,
  enableTrackedChanges,
  setEnableTrackedChanges,
  actionItemDisabled,
  className,
}) => {
  const { studyId, activityId, requestId } = useParams()
  const model = !!activityId ? "activity" : "request"

  // "activity" model
  const { data: activity } = useGetActivity(studyId, activityId)
  const { data: authoringVersions } = useGetAuthoringVersions(activity?.authoring_study_pk, {
    enabled: model === "activity",
  })
  const { data: documents } = useGetStudyDocuments(activity?.authoring_study_pk, { enabled: model === "activity" })
  const { data: commentTypes } = useGetCommentTypes()
  const { data: userSettings } = useUserSettings()

  // "request" model
  const { data: requestFiles } = useGetRequestDocuments(requestId, {
    enabled: model === "request",
  })

  // value watch
  // filter out items from field options for "activity" model
  const formVersValue = Form.useWatch(["review_approve_data", "form_version"], form)
  const formCompareVersValue = Form.useWatch(["review_approve_data", "form_compare_version"], form)
  // hide/show conditional form fields
  const formTypesValue = Form.useWatch(["review_approve_data", "form_types"], form)
  const filesValue = Form.useWatch(["review_approve_data", "documents"], form)

  const versionCompareOptions = React.useMemo(() => {
    if (!authoringVersions || !userSettings) return []
    return [
      ...authoringVersions.map((item) => ({
        id: item.version_num,
        text: `Version ${item.version_num.toFixed(1)} - Created by ${item.created_by_fullname} on ${dayjs(item.date_created).format(userSettings.date_format)} (${item.notes})`,
      })),
    ]
  }, [authoringVersions])

  return (
    <div className={`review-items-workflow-fields${className ? ` ${className}` : ""}`}>
      {/* forms */}
      {model === "activity" && (
        <div className="workflow-card rev-items-workflow-forms">
          <Form.Item
            name={["review_approve_data", "study_id"]}
            hidden
          />

          <Form.Item
            label="Form(s)"
            name={["review_approve_data", "form_types"]}
            rules={[
              {
                required: (!filesValue || filesValue.length === 0) && !actionItemDisabled,
                message: "At least one form or file needs to be selected",
              },
            ]}
            dependencies={[["review_approve_data", "documents"]]}
          >
            <FileTypeField disabled={actionItemDisabled} />
          </Form.Item>
          {formTypesValue && formTypesValue.length > 0 && !actionItemDisabled ? (
            <>
              <Form.Item
                label="Version"
                name={["review_approve_data", "form_version"]}
                rules={[{ required: true }]}
              >
                <Select
                  // filtering out version compare field selection
                  options={versionCompareOptions?.filter((item) => item.id !== formCompareVersValue)}
                />
              </Form.Item>
              <Form.Item
                label="Comment types"
                name={["review_approve_data", "comment_types"]}
                rules={[{ required: true }]}
                className="rev-items-workflow-comments"
              >
                <Select
                  mode="multiple"
                  options={commentTypes?.map((comment) => comment.name)}
                  allowClear
                />
              </Form.Item>
              <div className="rev-items-track-changes">
                <WorkflowModalHeading
                  title="Enable tracked changes"
                  switchProps={{
                    checked: enableTrackedChanges,
                    onChange: (checked) => setEnableTrackedChanges(checked),
                  }}
                />
                {enableTrackedChanges && (
                  <div className="track-changes-content">
                    <Form.Item
                      name={["review_approve_data", "form_compare_version"]}
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder="Version to compare"
                        // filtering out version field selection
                        options={versionCompareOptions?.filter((item) => item.id !== formVersValue)}
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Form.Item
                name={["review_approve_data", "form_version"]}
                hidden
              />
              <Form.Item
                name={["review_approve_data", "comment_types"]}
                hidden
              />
            </>
          )}
        </div>
      )}

      {/* files */}
      <div className="workflow-card">
        <Form.Item
          name={["review_approve_data", "documents"]}
          label="Files"
          rules={[
            {
              required: (!formTypesValue || formTypesValue.length === 0) & !actionItemDisabled,
              message: "At least one file or form needs to be selected",
            },
          ]}
          dependencies={[["review_approve_data", "form_types"]]}
        >
          <Select
            mode="multiple"
            options={model === "activity" ? documents : requestFiles}
            disabled={actionItemDisabled}
          />
        </Form.Item>
      </div>
    </div>
  )
}

FilesFormsWorkFields.propTypes = {
  model: PropTypes.oneOf(["activity", "request"]),
}

export default FilesFormsWorkFields
